import { useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { buildPath, type ApiResponse } from "@shared/contracts";
import { entityKeyRoot } from "../../../helpers";
import type {
  WithIdVariables,
  WriteApiOptions,
  WriteApiWithIdOptions,
} from "../../../interfaces";
import { api, type ApiError } from "../../../providers";
import { useCustomMutation } from "../useCustomMutation";
import type {
  AnyWriteVariables,
  WriteApiHook,
  WriteApiImplOptions,
  WriteMethod,
} from "./Props";

/**
 * Shared engine of `usePostApi` / `usePutApi` / `usePatchApi`: path building
 * (`serviceName[/:id]`), envelope-preserving mutationFn, auto-invalidation of
 * the entity's key root, and the `(data, message, variables)` callback. Not
 * exported from the crud barrel — consumers use the per-verb hooks.
 */
export function useWriteApi<IResponse, IRequest>(
  method: WriteMethod,
  options:
    | WriteApiOptions<IResponse, IRequest>
    | WriteApiWithIdOptions<IResponse, IRequest>
): UseMutationResult<
  ApiResponse<IResponse>,
  ApiError,
  AnyWriteVariables<IRequest>
> {
  // The two option shapes only disagree on the variables type; a single
  // union-variable view keeps the implementation to one code path.
  const {
    serviceName,
    idRequired = false,
    invalidateKeys,
    onSuccess,
    ...mutationOptions
  } = options as WriteApiImplOptions<IResponse, IRequest>;

  const queryClient = useQueryClient();

  return useCustomMutation<
    ApiResponse<IResponse>,
    ApiError,
    AnyWriteVariables<IRequest>
  >({
    ...mutationOptions,
    mutationFn: (variables) => {
      if (idRequired) {
        const { id, body } = variables as WithIdVariables<IRequest>;
        return api[method]<IResponse>(buildPath(serviceName, id), body);
      }
      return api[method]<IResponse>(serviceName, variables);
    },
    onSuccess: (response, variables) => {
      if (invalidateKeys !== false) {
        const keys = invalidateKeys ?? [entityKeyRoot(serviceName)];
        for (const queryKey of keys) {
          void queryClient.invalidateQueries({ queryKey });
        }
      }
      onSuccess?.(response.data, response.message, variables);
    },
  });
}

/**
 * Binds the engine to one HTTP method so each verb hook is a one-liner. The
 * returned function is a real hook (the `use` name keeps rules-of-hooks
 * satisfied); the cast narrows the union-typed engine onto the per-overload
 * `WriteApiHook` surface.
 */
export function createWriteApiHook(method: WriteMethod): WriteApiHook {
  function useVerbApi<IResponse, IRequest = void>(
    options:
      | WriteApiOptions<IResponse, IRequest>
      | WriteApiWithIdOptions<IResponse, IRequest>
  ) {
    return useWriteApi<IResponse, IRequest>(method, options);
  }
  return useVerbApi as WriteApiHook;
}
